import { Injectable, Injector, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { OfflineCacheService } from './offline-cache.service';
import { DoctorTariffService } from './doctor-tariff.service';
import { SurveySessionService } from './survey-session';
import { SurveyDocumentsStore } from './survey-documents-store.service';

export interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  middleName?: string;
  fullName: string;
  birthDate?: string;
  gender?: string;
  telegramId?: number;
  telegramUsername?: string;
  assignedDoctorId?: string;
}

export interface AuthResponse {
  access_token: string;
  user: AuthUser;
}

export interface InviteInfo {
  patient: {
    firstName?: string;
    lastName?: string;
    middleName?: string;
    email?: string | null;
    birthDate?: string | null;
    gender?: string | null;
  };
  doctor: {
    fullName: string;
    specialization?: string | null;
  } | null;
  expiresAt: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly injector = inject(Injector);
  private readonly router = inject(Router);
  private readonly offlineCache = inject(OfflineCacheService);
  private readonly doctorTariff = inject(DoctorTariffService);
  private readonly surveySession = inject(SurveySessionService);
  private readonly apiUrl = `${environment.apiUrl}/auth`;

  // Signals для реактивного состояния
  readonly currentUser = signal<AuthUser | null>(null);
  readonly isAuthenticated = signal<boolean>(false);

  constructor() {
    // Проверяем токен при инициализации
    this.checkAuth();
  }

  /**
   * Проверка авторизации по токену в localStorage
   */
  private checkAuth(): void {
    const token = this.getToken();
    const userJson = localStorage.getItem('user');

    if (token && userJson) {
      try {
        const user = JSON.parse(userJson);
        this.currentUser.set(user);
        this.isAuthenticated.set(true);
      } catch {
        this.logout();
      }
    }
  }

  /**
   * Регистрация нового пациента
   * После регистрации требуется подтверждение email, поэтому токен не сохраняется
   */
  register(data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    middleName: string;
    birthDate: string;
    gender: string;
    doctorCode: string;
    personalDataConsent: boolean;
    marketingConsent: boolean;
    privacyPolicyConsent: boolean;
  }): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.apiUrl}/register-patient`, data).pipe(
      tap(response => {
        // Сохраняем токен только если он не null (для completeWebRegistration)
        if (response.access_token) {
          this.handleAuthSuccess(response);
        }
      })
    );
  }

  /**
   * Логин пациента
   */
  login(email: string, password: string): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.apiUrl}/login-patient`, { email, password }).pipe(
      tap(response => this.handleAuthSuccess(response))
    );
  }

  /**
   * Получение данных пользователя по токену (для предзаполнения формы регистрации).
   * Бэкенд намеренно возвращает только non-sensitive поля — ДР/пол/отчество
   * пациент вводит сам при регистрации (эти данные не возвращаем в публичном
   * endpoint для защиты от утечки через случайно попавший к третьим лицам токен).
   */
  getUserByToken(token: string): Observable<{
    firstName?: string;
    lastName?: string;
    email?: string;
    hasDoctor: boolean;
    hasConsent: boolean;
  }> {
    return this.http.get<any>(`${this.apiUrl}/user-by-token/${token}`);
  }

  /**
   * Завершение регистрации из Telegram
   */
  completeWebRegistration(token: string, email: string, password: string): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.apiUrl}/complete-web-registration`, {
      token,
      email,
      password
    }).pipe(
      tap(response => this.handleAuthSuccess(response))
    );
  }

  /**
   * Получение информации о приглашении пациента врачом (без потребления токена).
   */
  getInviteInfo(token: string): Observable<InviteInfo> {
    return this.http.get<InviteInfo>(`${this.apiUrl}/invite/${token}`);
  }

  /**
   * Приём приглашения: установка пароля и согласий.
   */
  acceptInvite(data: {
    token: string;
    password: string;
    personalDataConsent: boolean;
    privacyPolicyConsent: boolean;
    marketingConsent?: boolean;
  }): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${this.apiUrl}/accept-invite`, data)
      .pipe(tap(response => this.handleAuthSuccess(response)));
  }

  /**
   * Генерация ссылки для привязки Telegram
   */
  generateTelegramLink(): Observable<{
    token: string;
    telegramLink: string;
    expiresAt: string;
    expiresIn: string;
  }> {
    return this.http.post<any>(`${this.apiUrl}/generate-telegram-link`, {});
  }

  /**
   * Обновление профиля пациента
   */
  updateProfile(data: {
    firstName: string;
    lastName: string;
    middleName?: string;
    birthDate?: string;
    gender?: string;
  }): Observable<AuthUser> {
    return this.http.patch<AuthUser>(`${this.apiUrl}/update-profile`, data).pipe(
      tap(user => {
        localStorage.setItem('user', JSON.stringify(user));
        this.currentUser.set(user);
      })
    );
  }

  /**
   * Смена пароля
   */
  changePassword(currentPassword: string, newPassword: string): Observable<{ success: boolean; message?: string }> {
    return this.http.post<{ success: boolean; message?: string }>(`${this.apiUrl}/change-password-patient`, {
      currentPassword,
      newPassword
    });
  }

  /**
   * Запрос сброса пароля пациента
   */
  forgotPassword(email: string): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(
      `${this.apiUrl}/forgot-password-patient`, { email }
    );
  }

  /**
   * Проверка валидности токена сброса пароля
   */
  validateResetToken(token: string): Observable<{ valid: boolean; message?: string }> {
    return this.http.get<{ valid: boolean; message?: string }>(
      `${this.apiUrl}/validate-reset-token`, { params: { token, type: 'patient' } }
    );
  }

  /**
   * Сброс пароля по токену
   */
  resetPassword(token: string, newPassword: string): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(
      `${this.apiUrl}/reset-password`, { token, newPassword, type: 'patient' }
    );
  }

  /**
   * Подтверждение email по токену
   */
  verifyEmail(token: string, type: 'patient' | 'doctor'): Observable<{ success: boolean; message: string }> {
    return this.http.get<{ success: boolean; message: string }>(
      `${this.apiUrl}/verify-email/${token}?type=${type}`
    );
  }

  /**
   * Выход из системы
   */
  logout(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    this.currentUser.set(null);
    this.isAuthenticated.set(false);
    // Сбрасываем root-сервисы с in-memory state — иначе при перелогине
    // новый пациент видит данные предыдущего (тариф врача, активная сессия опроса).
    this.doctorTariff.reset();
    this.surveySession.clear();
    // Документы опросника: иначе опрос статусов продолжал бы ходить уже без
    // токена, каждый 401 снова вызывал бы logout, а запоздавший 401 выбивал
    // бы новую сессию. Заодно документы прежнего пациента не остаются в
    // памяти. Через Injector, а не inject() в поле: хранилище через HTTP-
    // клиент и интерцептор само зависит от AuthService — был бы цикл DI.
    this.injector.get(SurveyDocumentsStore).reset();
    // Fire-and-forget: чистим локальные медданные, чтобы их не увидел
    // следующий пользователь на том же устройстве.
    void this.offlineCache.clearAllUserData().catch(() => undefined);
    void this.clearServiceWorkerCache();
    this.router.navigate(['/login']);
  }

  private async clearServiceWorkerCache(): Promise<void> {
    if (typeof caches === 'undefined') return;
    try {
      const keys = await caches.keys();
      await Promise.all(keys.map(key => caches.delete(key)));
    } catch {
      // В приватном режиме или при недоступном Cache API молча игнорируем
    }
  }

  /**
   * Получение токена
   */
  getToken(): string | null {
    return localStorage.getItem('token');
  }

  /**
   * Обработка успешной авторизации
   */
  private handleAuthSuccess(response: AuthResponse): void {
    localStorage.setItem('token', response.access_token);
    localStorage.setItem('user', JSON.stringify(response.user));
    this.currentUser.set(response.user);
    this.isAuthenticated.set(true);
  }
}
